import { useState, useMemo } from "react";
import { Typography } from "@grantyd/ui";
import styles from "./Terms.module.css";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useJsonDocContent } from "@/hooks/useJsonDocContent";
import classNames from "classnames";
import { Link } from "react-router";

// Create a direct HTML renderer component
const DirectHtmlContent = ({
  html,
  tabKey,
}: {
  html: string;
  tabKey: string;
}) => {
  return (
    <div
      className={styles.directContent}
      data-testid={`direct-content-${tabKey}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

interface TermsProps {
  tab: "terms" | "privacy";
}

function Terms({ tab = "terms" }: TermsProps) {
  const [activeTab, setActiveTab] = useState<TermsProps["tab"]>(tab);
  const isDesktop = useMediaQuery("(min-width: 768px)");

  // Fetch JSON content
  const { jsonContent, isLoading } = useJsonDocContent();

  // Define available tabs
  const availableTabs = useMemo<TermsProps["tab"][]>(
    () => ["terms", "privacy"],
    []
  );

  const safeActiveTab = useMemo(
    () => (availableTabs.includes(activeTab) ? activeTab : availableTabs[0]),
    [activeTab, availableTabs]
  );

  const currentTabTitle = useMemo(() => {
    const tabKey = safeActiveTab as "terms" | "privacy";
    return tabKey === "terms" ? "Terms & Conditions" : "Privacy Policy";
  }, [safeActiveTab]);

  // Get last updated date
  const lastUpdated = useMemo(() => {
    const tabKey = safeActiveTab as "terms" | "privacy";
    return jsonContent[tabKey]?.lastUpdated || "June 1, 2025";
  }, [jsonContent, safeActiveTab]);

  // Show loading state
  if (isLoading) {
    return (
      <section className={styles.termsContainer} data-testid="terms-section">
        <div className={styles.wrapper}>
          <div className={styles.header}>
            <Typography
              as="h1"
              variant={isDesktop ? "display-md" : "headline-lg"}
              color="primary"
              className={styles.title}
            >
              Loading Content
            </Typography>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.termsContainer} data-testid="terms-section">
      <div className={styles.wrapper}>
        {/* Header section */}
        <div className={styles.header}>
          <Typography
            as="h1"
            variant={isDesktop ? "display-md" : "headline-lg"}
            color="primary"
            className={styles.title}
          >
            {currentTabTitle}
          </Typography>
          {lastUpdated && (
            <Typography variant="body-md" className={styles.lastUpdated}>
              {`Last Updated: ${lastUpdated}`}
            </Typography>
          )}
        </div>

        {/* Tab navigation */}
        <div className={styles.tabsContainer}>
          <div className={styles.tabs}>
            {availableTabs.map((tab) => (
              <Link
                to={"/" + tab.split("/").at(-1)}
                replace
                onClick={() => setActiveTab(tab)}
              >
                <button
                  key={tab}
                  className={classNames(styles.tab, {
                    [styles.active]: safeActiveTab === tab,
                  })}
                  data-testid={`${tab}-tab`}
                >
                  <Typography
                    variant="label-lg"
                    className={safeActiveTab === tab ? "" : styles.inactiveTab}
                  >
                    {tab === "terms" ? "Terms & Conditions" : "Privacy Policy"}
                  </Typography>
                </button>
              </Link>
            ))}
          </div>
        </div>

        {/* Content section */}
        <div className={styles.content} data-testid="terms-content">
          {jsonContent[safeActiveTab as "terms" | "privacy"]?.html ? (
            <DirectHtmlContent
              html={jsonContent[safeActiveTab as "terms" | "privacy"].html}
              tabKey={safeActiveTab}
            />
          ) : (
            <div className={styles.noContent}>
              <Typography variant="body-lg">
                No content available. Please try refreshing.
              </Typography>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default Terms;
